import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './ProfileEdit.module.css';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import {
    contactInfoSchema,
    studentProfileSchema,
    teacherProfileSchema,
    passwordSchema,
    ContactInfoFormValues,
    StudentProfileFormValues,
    TeacherProfileFormValues,
    PasswordFormValues
} from '../../types/profileSchemas';
import { useAuth } from 'hooks/auth';
import { UserRole } from 'types/auth';
import { api } from 'api/axios';
import Loading from 'components/Loading/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faInfoCircle, faPen, faEye, faEyeSlash, faUpload } from '@fortawesome/free-solid-svg-icons';

// Using a generic avatar placeholder
const DEFAULT_AVATAR = "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";

const ProfileEdit: React.FC = () => {
    const auth = useAuth();
    const userRole = auth?.user?.role;

    const [avatarPreview, setAvatarPreview] = useState<string>(DEFAULT_AVATAR);
    const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [username, setUsername] = useState<string>('');

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Profile Info Form (Combined for multiple roles)
    const {
        register: registerProfile,
        handleSubmit: handleSubmitProfile,
        reset: resetProfile,
        formState: { errors: profileErrors, isSubmitting: isSubmittingProfile },
    } = useForm<any>({
        resolver: zodResolver(
            userRole === UserRole.STUDENT ? studentProfileSchema : 
            userRole === UserRole.TEACHER ? teacherProfileSchema : 
            contactInfoSchema
        ),
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setIsLoading(true);
                const response = await api.get('/auth/me');
                const profile = response.data.data;

                if (userRole === UserRole.STUDENT) {
                    resetProfile({
                        fullName: profile.full_name || '',
                        grade: profile.grade || '',
                        section: profile.section || '',
                    });
                } else if (userRole === UserRole.TEACHER) {
                    resetProfile({
                        fullName: profile.full_name || '',
                    });
                } else {
                    resetProfile({
                        fullName: profile.full_name || '',
                        email: profile.email || '',
                        contactNumber: profile.contact || '',
                    });
                }

                if (profile.profile_url) {
                    setAvatarPreview(profile.profile_url);
                }
                if (profile.username) {
                    setUsername(profile.username);
                }
                setError(null);
            } catch (err: any) {
                console.error('Error fetching profile:', err);
                setError(err.response?.data?.message || 'Failed to load profile');
            } finally {
                setIsLoading(false);
            }
        };

        if (userRole === UserRole.SCHOOL_ADMIN || userRole === UserRole.STUDENT || userRole === UserRole.TEACHER) {
            fetchProfile();
        } else {
            setIsLoading(false);
        }
    }, [userRole, resetProfile]);

    // Password Form
    const {
        register: registerPassword,
        handleSubmit: handleSubmitPassword,
        formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
        reset: resetPassword,
    } = useForm<PasswordFormValues>({
        resolver: zodResolver(passwordSchema),
    });

    const onAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setAvatarPreview(url);
            setProfilePhoto(file);
        }
    };

    const onSaveAvatar = async () => {
        if (!profilePhoto) {
            alert('Please select an image first.');
            return;
        }
        try {
            setIsUploading(true);
            const { uploadImage } = await import('api/upload');
            const profileUrl = await uploadImage(profilePhoto, 'profiles');

            await api.patch('/auth/update-profile', {
                profile_url: profileUrl,
            });
            alert('Avatar updated successfully!');
            setProfilePhoto(null);
        } catch (err: any) {
            console.error('Error updating avatar:', err);
            alert(err.response?.data?.message || 'Failed to update avatar');
        } finally {
            setIsUploading(false);
        }
    };

    const onUpdateProfile = async (data: any) => {
        try {
            setIsUploading(true);

            const payload: any = {
                full_name: data.fullName,
            };

            if (userRole === UserRole.STUDENT) {
                payload.grade = data.grade;
                payload.section = data.section;
            } else if (userRole !== UserRole.TEACHER) {
                payload.email = data.email;
                payload.contact = data.contactNumber;
            }

            await api.patch('/auth/update-profile', payload);
            alert('Profile updated successfully!');
        } catch (err: any) {
            console.error('Error updating profile:', err);
            alert(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setIsUploading(false);
        }
    };

    const onUpdatePassword = async (data: PasswordFormValues) => {
        try {
            const payload = {
                old_password: data.currentPassword,
                new_password: data.newPassword,
            };
            await api.patch('/auth/change-password', payload);
            alert('Password changed successfully!');
            resetPassword();
        } catch (err: any) {
            console.error('Error changing password:', err);
            alert(err.response?.data?.message || 'Failed to change password');
        }
    };

    if (isLoading) return <Loading fullPage message="Loading your profile..." />;
    if (error) return <div className={styles.container}>Error: {error}</div>;

    if (userRole !== UserRole.SCHOOL_ADMIN && userRole !== UserRole.STUDENT && userRole !== UserRole.TEACHER) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}>Profile Edit</h3>
                    <p>Current built UI is optimized for School Admins and Students. Your profile details can be managed here soon.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.profileGrid}>
                {/* Left Column */}
                <div className={styles.leftCol}>
                    {/* Section 1: Update Profile Picture */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.cardTitle}>Update Profile Picture</h3>
                        </div>
                        <div className={styles.avatarContainer}>
                            <div className={styles.avatarCircle} onClick={onAvatarClick}>
                                <img src={avatarPreview} alt="Profile Avatar" className={styles.avatarImage} />
                                <div className={styles.editBadge}>
                                    <FontAwesomeIcon icon={faUpload} />
                                </div>
                            </div>
                            <p className={styles.avatarHint}>Upload a professional headshot. JPEG or PNG, max 2MB.</p>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="image/*"
                            onChange={onFileChange}
                        />
                    </div>
                    <div className={styles.actions}>
                        <Button
                            className={styles.updateAvatarBtn}
                            fullWidth
                            onClick={onSaveAvatar}
                            isLoading={isUploading}
                        >
                            Update Image
                        </Button>
                    </div>
                </div>
                </div>

                {/* Right Column */}
                <div className={styles.rightCol}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.headerIcon}>
                                <FontAwesomeIcon icon={faUser} />
                            </div>
                            <h3 className={styles.cardTitle}>{userRole === UserRole.STUDENT || userRole === UserRole.TEACHER ? 'Edit Your Profile' : 'Edit Contact Information'}</h3>
                        </div>
                        <form className={styles.form} onSubmit={handleSubmitProfile(onUpdateProfile)}>
                            <div className={styles.inputGrid}>
                                <div className={styles.readOnlyField}>
                                    <span className={styles.readOnlyLabel}>Username:</span>
                                    <span className={styles.readOnlyValue}>{username}</span>
                                </div>
                        <InputWithLabel
                            label="Full Name:"
                            placeholder="Enter full name"
                            error={profileErrors.fullName?.message as string}
                            {...registerProfile('fullName')}
                        />
                        {userRole === UserRole.STUDENT ? (
                            <>
                                <InputWithLabel
                                    label="Grade:"
                                    placeholder="Enter grade (e.g. 10th)"
                                    error={profileErrors.grade?.message as string}
                                    {...registerProfile('grade')}
                                />
                                <InputWithLabel
                                    label="Section:"
                                    placeholder="Enter section (e.g. A)"
                                    error={profileErrors.section?.message as string}
                                    {...registerProfile('section')}
                                />
                            </>
                        ) : userRole === UserRole.TEACHER ? null : (
                            <>
                                <InputWithLabel
                                    label="Email Address:"
                                    placeholder="Enter email address"
                                    error={profileErrors.email?.message as string}
                                    {...registerProfile('email')}
                                />
                                <InputWithLabel
                                    label="Contact Number:"
                                    placeholder="Enter contact number"
                                    error={profileErrors.contactNumber?.message as string}
                                    {...registerProfile('contactNumber')}
                                />
                            </>
                        )}
                        </div>
                        <div className={styles.actions}>
                                <Button
                                    type="submit"
                                    className={styles.updateBtn}
                                isLoading={isSubmittingProfile || isUploading}
                            >
                                Update Profile
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Section 3: Set New Password */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon}>
                            <FontAwesomeIcon icon={faLock} />
                        </div>
                        <h3 className={styles.cardTitle}>Set New Password</h3>
                    </div>
                    <form className={styles.form} onSubmit={handleSubmitPassword(onUpdatePassword)}>
                        <div className={styles.passwordField}>
                            <InputWithLabel
                                label="Current Password"
                                type={showCurrentPassword ? "text" : "password"}
                                placeholder="Enter current password"
                                error={passwordErrors.currentPassword?.message}
                                className={styles.passwordInput}
                                iconRight={
                                    <FontAwesomeIcon 
                                        icon={showCurrentPassword ? faEyeSlash : faEye} 
                                        className={styles.eyeIcon} 
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)} 
                                    />
                                }
                                {...registerPassword('currentPassword')}
                            />
                        </div>

                        <div className={styles.inputGrid}>
                            <div className={styles.passwordField}>
                                <InputWithLabel
                                    label="New Password"
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    error={passwordErrors.newPassword?.message}
                                    className={styles.passwordInput}
                                    iconRight={
                                        <FontAwesomeIcon 
                                            icon={showNewPassword ? faEyeSlash : faEye} 
                                            className={styles.eyeIcon} 
                                            onClick={() => setShowNewPassword(!showNewPassword)} 
                                        />
                                    }
                                    {...registerPassword('newPassword')}
                                />
                            </div>

                            <div className={styles.passwordField}>
                                <InputWithLabel
                                    label="Confirm New Password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm new password"
                                    error={passwordErrors.confirmPassword?.message}
                                    className={styles.passwordInput}
                                    iconRight={
                                        <FontAwesomeIcon 
                                            icon={showConfirmPassword ? faEyeSlash : faEye} 
                                            className={styles.eyeIcon} 
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                                        />
                                    }
                                    {...registerPassword('confirmPassword')}
                                />
                            </div>
                        </div>

                        <div className={styles.infoBlock}>
                            <FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
                            <span>Password must be at least 8 characters long and contain both numbers and special characters.</span>
                        </div>

                        <div className={styles.actions}>
                            <Button
                                type="submit"
                                className={styles.updateBtn}
                                isLoading={isSubmittingPassword}
                            >
                                Change Password
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
    );
};

export default ProfileEdit;
